import { css, html, TemplateResult, unsafeCSS } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { Api } from "../api/Api.js";
import { Entity, Engine } from "../engine/Engine.js";
import lfUrl from "../fonts/lg.ttf";
import { MediaImageType } from "../types/ShopifyImageTypes.js";
import { BaseElement } from "../ui/base/BaseElement.js";
import { ImageGallery } from "../ui/ImageGallery.js";
import { LoadingElement } from "../ui/LoadingElement.js";
import { LoadingElement2 } from "../ui/LoadingElement2.js";
import { Slider } from "../ui/Slider.js";
import { waitTill } from "../util/Wait.js";
import {
  setConfiguratorData,
  ConfiguratorVariant,
} from "./ConfiguratorData.js";
import { SugarElement } from "./SugarCanvas.js";
import { ConfiguratorViewerController } from "./SugarConfiguratorViewerController.js";
import { configuratorSVGs } from "./SugarConfiguratorSVGs.js";
import("../postporcesses/GTAOPostProcessElement.js");
import("../postporcesses/GTAOAccumulatePostProcessElement.js");
import("../postporcesses/SugarTAAPostProcessElement.js");
import("../postporcesses/SSGIPostProcessElement.js");
import("../postporcesses/ProgressiveLightmapGroundElement.js");
import("../ui/LoadingElement2.js");
import("../ui/ImageGallery.js");
import("../camera/CameraAnimationElement");
import("../configurator/SmoothOrbitElement.js");

const style = css`
  @font-face {
    font-family: "lg";
    src: url("${unsafeCSS(lfUrl)}") format("truetype");
  }

  :host {
    display: block;
    box-sizing: border-box;
    aspect-ratio: 1.15;
  }
  sugar-configurator-viewer {
    position: relative;
  }

  .sugar-configurator-container {
    container-type: inline-size;
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
  }

  sugar-canvas {
    width: 100%;
    height: 100%;
    display: block;
    opacity: 1;
    position: relative;
  }

  .sugar-thumb-button {
    width: 112px;
    height: 112px;
    opacity: 0.6;
    object-fit: cover;
  }

  .sugar-thumb-button:hover {
    opacity: 1;
  }

  .sugar-thumb-button.active {
    opacity: 1;
  }

  .sugar-3d-button {
    border: none;
    background-color: rgba(52, 68, 86, 0.7);
    color: white;
  }

  .sugar-meter-button {
    position: absolute;
    bottom: 10px;
    left: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 0px solid black;
    background-color: #eeeeee;
  }

  .sugar-meter-button.active {
    background-color: black;
  }

  .sugar-meter-button.active > .sugar-meter-button-svg {
    background: url("${unsafeCSS(configuratorSVGs.meterButtonActive32)}");
  }

  .sugar-meter-button-svg {
    width: 32px;
    height: 32px;
    border: none;
    background: url("${unsafeCSS(configuratorSVGs.meterButton32)}");
  }

  .sugar-3d-button-animate {
    background: url("${unsafeCSS(configuratorSVGs.button3DAnimate36)}");
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 36px;
    border: none;
    position: absolute;
  }

  .sugar-3d-button::before {
    content: "3D";
  }

  .sugar-thumbs-container {
    display: flex;
    justify-content: flex-start;
    align-content: center;
    flex-direction: column;
    gap: 8px;
  }

  center {
    flex: 1;
    position: relative;
    display: flex;
    width: 100%;
    min-width: 0;
  }

  left {
    margin-right: 8px;
    height: 100%;
    position: relative;
    width: 128px;
  }

  /* Parent width 764px'den küçükse gizle */
  @container (max-width: 763px) {
    left {
      display: none;
    }
  }

  /* Fallback: viewport width 764px'den küçükse gizle (container query desteklenmiyorsa) */
  @media (max-width: 763px) {
    :host left {
      display: none;
    }
  }

  .sugar-image {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: -2;
    opacity: 0;
    top: 0px;
    left: 0px;
    object-fit: contain;
    object-position: center;
  }

  .pass-button {
    width: 40px;
    height: 40px;
    border: none;
    background: #3a4a5b;
    border-radius: 50px;
    opacity: 0.7;
    touch-action: manipulation;
  }

  .passive-button {
    display: none;
  }

  .pass-button:hover {
    opacity: 0.9;
  }

  .pass-button::before {
    content: "";
    background: url("${unsafeCSS(configuratorSVGs.scrollDownArrow)}") no-repeat
      center;
    width: 20px;
    height: 40px;
    display: inline-block;
  }

  .left-button {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translate(0, -50%) rotate(90deg);
    z-index: 9;
  }

  .right-button {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translate(0, -50%) rotate(-90deg);
    z-index: 9;
  }

  .thumbnail-scroll-down {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 40px;
    background-color: rgba(56, 73, 91, 0.7);
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 10;
    border-radius: 0;
  }

  .sugar-3d-view-button {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 20px;
    background-color: rgba(102, 102, 102, 0.7);
    border: none;
    border-radius: 50px;
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    z-index: 10;
    transition: all 0.2s ease;
    backdrop-filter: blur(4px);
  }

  .sugar-3d-view-button:hover {
    background-color: rgba(102, 102, 102, 0.85);
    transform: translateX(-50%) scale(1.05);
  }

  .sugar-3d-view-button-icon {
    width: 14px;
    height: 14px;
    background: url("${unsafeCSS(configuratorSVGs.view3DIcon14)}") no-repeat
      center;
    background-size: contain;
    flex-shrink: 0;
  }

  .sugar-3d-view-button-text {
    white-space: nowrap;
  }

  /* .right-button::before {
    content: "\\e095";
    font-family: lg;
    font-size: xx-large;
  }

  .left-button::before {
    content: "\\e094";
    font-family: lg;
    font-size: xx-large;
  }*/

  @media (min-width: 999px) {
    /* .pass-button {
      display: none;
      background-color: #ffffff00;
    } */
    :host {
      aspect-ratio: 1.33;
    }

    .sugar-meter-button.active > .sugar-meter-button-svg {
      background: url("${unsafeCSS(configuratorSVGs.meterButtonActive40)}");
    }

    .sugar-meter-button-svg {
      width: 40px;
      height: 40px;
      border: none;
      background: url("${unsafeCSS(configuratorSVGs.meterButton40)}");
    }

    .sugar-3d-button-animate {
      background: url("${unsafeCSS(configuratorSVGs.button3DAnimate80)}");
      bottom: 0px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 80px;
      border: none;
      position: absolute;
    }
  }

  @media (min-width: 0px) and (max-width: 768px) {
    /* Mobile styles */
    left {
      margin-right: 0;
    }
  }
`;

@customElement("configurator-viewer")
export class SugarConfiguratorViewerEntity extends SugarElement {
  private _configuratorController?: ConfiguratorViewerController;

  @property({ type: Object, attribute: "variant" })
  private _variant?: ConfiguratorVariant;

  @property({ type: Boolean, attribute: "showMeter" })
  private _showMeter: boolean = false;

  @property({ type: String, attribute: "configurator-id" })
  private _configuratorId = "default";

  set configuratorId(value: string) {
    this._configuratorId = value;
    if (this._configuratorController) {
      this._configuratorController.configuratorId = value;
    }
  }

  get configuratorId(): string {
    return this._configuratorId;
  }

  get variant(): ConfiguratorVariant | undefined {
    return this._variant;
  }

  set variant(value: ConfiguratorVariant) {
    this._variant = value;
    if (this._configuratorController)
      this._configuratorController.variant = value;
  }

  get showMeter(): boolean {
    return this._showMeter;
  }

  set showMeter(value: boolean) {
    this._showMeter = value;
    if (this._configuratorController)
      this._configuratorController.showMeter = value;
  }

  fitCamera() {
    if (this._configuratorController) this._configuratorController?.fitCamera();
  }

  /**
   * Aktif kamerayı/orbit'i bozmadan, verilen preset ve boyutta tek seferlik
   * snapshot üretir.
   */
  async renderSnapshot(
    preset: Parameters<ConfiguratorViewerController["renderSnapshot"]>[0],
    options: Parameters<ConfiguratorViewerController["renderSnapshot"]>[1],
  ): Promise<Blob | null> {
    if (!this._configuratorController) return null;
    return this._configuratorController.renderSnapshot(preset, options);
  }

  createEntity(): Entity | undefined {
    this._configuratorController = new ConfiguratorViewerController();
    this._configuratorController.configuratorId = this._configuratorId;
    return this._configuratorController;
  }
}

@customElement("sugar-configurator-viewer")
export class SugarConfiguratorViewer extends BaseElement {
  static styles = style;
  protected override useShadowRoot = true;

  @property({ type: Object, attribute: "variant" })
  private _variant?: ConfiguratorVariant;

  @property({ type: Object, attribute: "gallery" })
  private _images?: MediaImageType[];

  @query("configurator-viewer")
  private configurator!: SugarConfiguratorViewerEntity;

  @query("loading-element")
  private loadingElement!: LoadingElement;

  @query("loading-element2")
  private loadingElement2!: LoadingElement2;

  /*@query(".sugar-image")
  private sugarImage!: HTMLImageElement;*/

  @query("sugar-canvas")
  private sugarCanvas!: HTMLElement;

  @query("image-gallery")
  private imageGallery!: ImageGallery;

  @query("generic-slider")
  private configuratorGallery!: Slider;

  @query(".sugar-scroll-content")
  private scrollContent!: HTMLElement;

  @query("#scrollDownBtn-product-template")
  private scrollDownButton!: HTMLButtonElement;

  set variant(value: ConfiguratorVariant) {
    this._variant = value;
    this.loadingElement.style.display = "";
    waitTill(() => this._configuratorReady).then(() => {
      if (this.configurator) this.configurator.variant = value;
    });
  }

  get variant(): ConfiguratorVariant | undefined {
    return this._variant;
  }

  private _firstGalleryImages = true;
  set images(value: MediaImageType[]) {
    this._images = value;

    this._filteredImages = this._images.filter(
      (image) => true, //checks.includes(image.alt)
    );

    if (this._firstGalleryImages) {
      this._firstGalleryImages = false;
      if (this._isSparseGallery()) {
        this._selectedIndex = 1;
      } else {
        this._selectedIndex = 0;
      }
    }

    this._syncEngineToSelectedSlide();

    // İçerik değiştiğinde scroll button görünürlüğünü güncelle
    this.requestUpdate();
  }

  get images(): MediaImageType[] | undefined {
    return this._images;
  }

  private _filteredImages: MediaImageType[] = [];
  /** En az bir ama iki görselden az galeri: varsayılan ana görünüm 3D değil, ilk galeri görseli. */
  private _isSparseGallery(): boolean {
    return false; //this._filteredImages.length > 2;
  }

  /** Slayt konumu `generic-slider` üzerindeki `start-index` ile senkron; burada yalnızca motor. */
  private _syncEngineToSelectedSlide(): void {
    const eng = Engine.activeEngine;
    if (!eng) return;
    if (this._selectedIndex === 0) {
      eng.resume();
    } else {
      eng.pause();
    }
  }

  private _configuratorReady = false;
  private _scrollListenerSetup = false;
  private _sliderTransitionListenerSetup = false;

  private _firstUpdated = false;
  async firstUpdated() {
    const data = await Api.fetchConfigurator(24);
    setConfiguratorData("24", data);
    this.configurator.configuratorId = "24";
    //this.loadingElement.style.display = "";
    this.loadingElement2.style.display = "";
    this.loadingElement.style.opacity = "0";
    this.dispatchEvent(new CustomEvent("cofigurator-ready"));
    this.shadowRoot?.addEventListener("configurator-updated", () => {
      this.loadingElement.style.display = "none";
      setTimeout(() => {
        this.loadingElement2.style.display = "none";
        this.loadingElement.style.opacity = "1";
        if (!this._firstUpdated) {
          setTimeout(() => {
            Engine.activeEngine.dispatchCustomEvent("scene-first-updated");
            this._firstUpdated = true;
          }, 100);
        }
      }, 20);
      this.dispatchEvent(
        new CustomEvent("configurator-updated", { bubbles: true }),
      );
    });
    this._configuratorReady = true;
    if (this._isSparseGallery()) {
      this._selectedIndex = 1;
    } else {
      this._selectedIndex = 0;
    }
    await this.updateComplete;
    this._syncEngineToSelectedSlide();
    this.addEventListener("configurator-updated", () => {
      this.onConfigClick();
    });
    this.requestUpdate();
  }

  override updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);

    // Scroll listener'ı sadece bir kez setup et
    if (
      !this._scrollListenerSetup &&
      this.scrollContent &&
      this.scrollDownButton
    ) {
      this.setupScrollListener();
      this._scrollListenerSetup = true;
    }

    // Slider transition listener'ı sadece bir kez setup et
    if (!this._sliderTransitionListenerSetup && this.configuratorGallery) {
      this.configuratorGallery.addEventListener("slider-transition-end", ((
        e: Event,
      ) => {
        const customEvent = e as CustomEvent;
        const newIndex = customEvent.detail.currentIndex;
        this._selectedIndex = newIndex;
        this.requestUpdate();
        const eng = Engine.activeEngine;
        if (eng) {
          if (newIndex === 0) {
            eng.resume();
          } else {
            eng.pause();
          }
        }
      }) as EventListener);
      this._sliderTransitionListenerSetup = true;
    }

    // İçerik değiştiğinde scroll button görünürlüğünü güncelle
    if (
      this._scrollListenerSetup &&
      this.scrollContent &&
      this.scrollDownButton
    ) {
      // DOM güncellemesi tamamlandıktan sonra kontrol et
      setTimeout(() => {
        this.updateScrollButtonVisibility();
      }, 0);
    }
  }

  private setupScrollListener() {
    if (this.scrollContent) {
      this.scrollContent.addEventListener("scroll", () => {
        this.updateScrollButtonVisibility();
      });
      // İlk görünürlük kontrolü
      this.updateScrollButtonVisibility();
    }
  }

  private updateScrollButtonVisibility() {
    if (!this.scrollContent || !this.scrollDownButton) return;

    const { scrollTop, scrollHeight, clientHeight } = this.scrollContent;
    const threshold = 5; // Küçük bir threshold değeri

    // Eğer scroll edilecek yer kalmadıysa butonu gizle
    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      this.scrollDownButton.style.display = "none";
    } else {
      // Scroll yukarı çıkarsa tekrar göster
      this.scrollDownButton.style.display = "flex";
    }
  }

  private onScrollDown() {
    if (!this.scrollContent) return;

    const { scrollTop, scrollHeight, clientHeight } = this.scrollContent;
    const scrollAmount = clientHeight * 0.5; // Container yüksekliğinin %80'i kadar scroll

    // Smooth scroll
    this.scrollContent.scrollTo({
      top: scrollTop + scrollAmount,
      behavior: "smooth",
    });
  }

  private _selectedIndex = 0;
  onImageClick(media: MediaImageType, index: number = 0) {
    this._selectedIndex = index;
    Slider.prototype.selectElement.call(
      this.configuratorGallery,
      this._selectedIndex,
    );
    this._syncEngineToSelectedSlide();
    this.requestUpdate();
  }

  onImageGalleryClick() {
    if (this._filteredImages.length > 0 && this._selectedIndex >= 0) {
      // Open gallery with all images, starting from current selected image
      const galleryIndex = this._selectedIndex;
      this.imageGallery.open(this._filteredImages, galleryIndex);
    }
  }

  onConfigClick() {
    this._selectedIndex = 0;
    Slider.prototype.selectElement.call(
      this.configuratorGallery,
      this._selectedIndex,
    );

    this._syncEngineToSelectedSlide();
    this.requestUpdate();
  }

  move(add: number) {
    // Metodları doğrudan Slider prototype'ından çağır

    Engine.activeEngine.pause();

    if (add > 0) {
      Slider.prototype.nextElement.call(this.configuratorGallery);
    } else {
      Slider.prototype.previousElement.call(this.configuratorGallery);
    }
    this._selectedIndex = this.configuratorGallery.currentIndex;
    this.requestUpdate();
  }

  set showMeter(value: boolean) {
    this.configurator.showMeter = value;
    this.requestUpdate();
  }

  toggleMeter() {
    if (this.configurator && this.configurator.variant) {
      this.configurator.showMeter = !this.configurator.showMeter;
    }
    this.requestUpdate();
  }

  onLoad() {
    this.loadingElement.style.display = "none";
  }

  onLoadError(e: Event) {
    if ((e.target as HTMLImageElement).src.includes("png"))
      (e.target as HTMLImageElement).src = (
        e.target as HTMLImageElement
      ).src.replace("png", "jpg");
    else if ((e.target as HTMLImageElement).src.includes("jpg"))
      (e.target as HTMLImageElement).src = (
        e.target as HTMLImageElement
      ).src.replace("jpg", "jpeg");
    else if ((e.target as HTMLImageElement).src.includes("jpeg"))
      (e.target as HTMLImageElement).src = (
        e.target as HTMLImageElement
      ).src.replace("jpeg", "webp");
    else this.loadingElement.style.display = "none";
  }

  protected renderHtml(): TemplateResult {
    return html`
      <div class="sugar-configurator-container" style="">
        <left style=" ">
          <div class="sugar-scroll-content" style="overflow:auto;height:100%">
            <div class="sugar-thumbs-container">
              <button
                class="sugar-3d-button sugar-thumb-button ${this
                  ._selectedIndex == 0
                  ? "active"
                  : ""}"
                @click=${() => this.onConfigClick()}
              ></button>
              ${this._filteredImages?.map((media, i) => {
                return html`<img
                  loading="lazy"
                  @error=${this.onLoadError}
                  src="${media.thumb?.src}"
                  srcset="${media.thumb?.srcset}"
                  class="sugar-thumb-button ${this._selectedIndex == i + 1
                    ? "active"
                    : ""}"
                  @click=${() => this.onImageClick(media, i + 1)}
                />`;
              })}
            </div>
          </div>

          <button
            class="thumbnail-scroll-down"
            id="scrollDownBtn-product-template"
            aria-label="Translation missing: tr.product.gallery.scroll_down"
            style="display: flex;"
            @click=${() => this.onScrollDown()}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 3.5V13.5M8.5 13.5L13.5 8.5M8.5 13.5L3.5 8.5"
                stroke="white"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>
          </button>
        </left>

        <center>
          <loading-element style="opacity: 0;"></loading-element>
          <loading-element2 text="3D Model Yükleniyor..."></loading-element2>

          <button
            class="left-button pass-button ${this._filteredImages.length == 0
              ? "passive-button"
              : ""}"
            @click=${() => this.move(-1)}
          ></button>
          <button
            class="right-button pass-button ${this._filteredImages.length == 0
              ? "passive-button"
              : ""}"
            @click=${() => this.move(1)}
          ></button>

          <generic-slider
            style="width:100%"
            not-touch-active-index="0"
            current-index=${this._selectedIndex}
          >
            <sugar-canvas>
              <smooth-orbit></smooth-orbit>
              <configurator-viewer>
                <sugar-taa sample-level="1"></sugar-taa>
                <camera-animation></camera-animation>
              </configurator-viewer>
              <div class="sugar-3d-button-animate"></div>
              <div
                class="sugar-meter-button ${this.configurator?.showMeter
                  ? "active"
                  : ""}"
              >
                <button
                  class="sugar-meter-button-svg"
                  @click=${() => this.toggleMeter()}
                ></button>
              </div>
            </sugar-canvas>

            ${this._filteredImages?.map((media, i) => {
              return html`<div
                style="position:relative;width:100%;height:100%;display:block;"
              >
                <img
                  loading="lazy"
                  src="${media.main?.src}"
                  srcset="${media.main?.srcset}"
                  @error=${this.onLoadError}
                  @load=${this.onLoad}
                  @click=${this.onImageGalleryClick}
                  style="object-fit:contain;display:block;width:100%;height:100%;cursor: ${this
                    ._selectedIndex > 0
                    ? "pointer"
                    : "default"}"
                />
              </div>`;
            })}
          </generic-slider>
          ${this._selectedIndex > 0
            ? html` <button
                class="sugar-3d-view-button"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  this.onConfigClick();
                }}
              >
                <div class="sugar-3d-view-button-icon"></div>
                <span class="sugar-3d-view-button-text">3 Boyutlu İncele</span>
              </button>`
            : ``}
          <image-gallery></image-gallery>
        </center>
      </div>
    `;
  }
}
